import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Briefcase, FolderKanban, FileText, Receipt, ArrowRight, FileCheck, X } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import Shell from "@/components/Shell";
import { Card, PageHeader, StatusBadge, Spinner } from "@/components/ui-kit";
import { formatGBP } from "@/lib/format";
import api from "@/lib/api";

const MONTH_LABEL = (key) => {
  // key is "YYYY-MM"
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-GB", { month: "short", year: "2-digit" });
};

const RevenueChart = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dashboard/revenue-by-month").then((r) => setData(r.data.months));
  }, []);

  if (!data) {
    return (
      <Card className="p-6">
        <div className="flex justify-center py-10"><Spinner /></div>
      </Card>
    );
  }

  const chartData = data.map((m) => ({
    month: MONTH_LABEL(m.month),
    Paid: Math.round(m.paid),
    Awaiting: Math.round(m.awaiting),
    Other: Math.round(m.other),
    _total: m.total,
  }));
  const total = chartData.reduce((acc, d) => acc + (d._total || 0), 0);
  const paidTotal = chartData.reduce((acc, d) => acc + (d.Paid || 0), 0);
  const awaitingTotal = chartData.reduce((acc, d) => acc + (d.Awaiting || 0), 0);

  return (
    <Card className="p-6 mb-8" data-testid="revenue-chart">
      <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-semibold text-zinc-950">Revenue by Month</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Last 12 months — based on invoiced projects (Xero status where available)</p>
        </div>
        <div className="flex items-center gap-5 text-xs">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">12-mo total</p>
            <p className="font-display text-lg font-bold text-zinc-950">{formatGBP(total)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-emerald-600">Paid</p>
            <p className="font-display text-lg font-bold text-emerald-700">{formatGBP(paidTotal)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-amber-600">Awaiting</p>
            <p className="font-display text-lg font-bold text-amber-700">{formatGBP(awaitingTotal)}</p>
          </div>
        </div>
      </div>
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
            <XAxis dataKey="month" stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => v >= 1000 ? `£${Math.round(v / 1000)}k` : `£${v}`} />
            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.04)" }}
              contentStyle={{ borderRadius: "10px", borderColor: "#e4e4e7", fontSize: "12px" }}
              formatter={(v) => formatGBP(v)}
            />
            <Legend wrapperStyle={{ fontSize: "12px" }} />
            <Bar dataKey="Paid" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
            <Bar dataKey="Awaiting" stackId="a" fill="#f59e0b" radius={[0, 0, 0, 0]} />
            <Bar dataKey="Other" stackId="a" fill="#71717a" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

const StatCard = ({ icon: Icon, label, value, to, testId }) => {
  const inner = (
    <Card className="p-5 group hover:border-zinc-300 hover:shadow-sm transition-all cursor-pointer">
      <div className="flex items-start justify-between mb-6">
        <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">{label}</p>
        <div className="w-8 h-8 rounded-lg bg-zinc-50 border border-zinc-100 grid place-items-center group-hover:bg-zinc-950 group-hover:border-zinc-950 transition-colors">
          <Icon className="w-4 h-4 text-zinc-500 group-hover:text-white transition-colors" />
        </div>
      </div>
      <p className="font-display text-4xl font-bold text-zinc-950">{value}</p>
    </Card>
  );
  return to ? <Link to={to} data-testid={testId}>{inner}</Link> : <div data-testid={testId}>{inner}</div>;
};

const ProofActivityBanner = () => {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    api.get("/proofs/unseen").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);
  if (dismissed || items.length === 0) return null;
  return (
    <div className="mb-6 rounded-2xl border-2 border-amber-300 bg-amber-50 p-5 flex items-start gap-4" data-testid="dashboard-proof-banner">
      <div className="w-10 h-10 rounded-xl bg-amber-500 text-white grid place-items-center shrink-0">
        <FileCheck className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display text-base font-bold text-amber-950">
          {items.length} proof{items.length !== 1 ? "s have" : " has"} new client feedback
        </p>
        <p className="text-xs text-amber-900/70 mb-2">Open each one to review and the badge clears.</p>
        <ul className="space-y-1">
          {items.slice(0, 5).map((p) => (
            <li key={p.id} className="text-sm">
              <Link
                to={`/projects/${p.project_id}?from=proofing`}
                className="inline-flex items-center gap-1.5 text-amber-900 hover:text-amber-950 font-medium underline underline-offset-2"
                data-testid={`dashboard-proof-banner-link-${p.id}`}
              >
                {p.client_name && <span className="text-amber-800/60">{p.client_name} ·</span>}
                <span>{p.project_name}</span>
                {p.version ? <span className="text-amber-800/70">· v{p.version}</span> : null}
                <ArrowRight className="w-3 h-3" />
              </Link>
            </li>
          ))}
          {items.length > 5 && (
            <li className="text-xs text-amber-800/70">+ {items.length - 5} more — see the Proofing tab</li>
          )}
        </ul>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="p-1.5 rounded-lg text-amber-700 hover:bg-amber-100 transition-colors"
        title="Dismiss until next refresh"
        data-testid="dashboard-proof-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Shell><div className="flex justify-center py-20"><Spinner /></div></Shell>;

  return (
    <Shell>
      <PageHeader
        testId="dashboard-header"
        title="Welcome back, Paul"
        subtitle="Overview of your studio — projects, quotes and things moving."
      />

      <ProofActivityBanner />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testId="stat-clients" icon={Users} label="Total Clients" value={stats.total_clients} to="/clients" />
        <StatCard testId="stat-projects" icon={Briefcase} label="Total Projects" value={stats.total_projects} to="/projects" />
        <StatCard testId="stat-active" icon={FolderKanban} label="Active Projects" value={stats.active_projects} to="/projects" />
        <StatCard testId="stat-quotes" icon={FileText} label="Total Quotes" value={stats.total_quotes} to="/quotes" />
      </div>

      <RevenueChart />

      <Card className="p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-display text-xl font-semibold text-zinc-950">Active Tasks</h2>
            <p className="text-xs text-zinc-500 mt-0.5">Tasks across your live projects</p>
          </div>
          <span className="text-xs font-semibold text-zinc-500 bg-zinc-100 rounded-full px-2.5 py-1">{stats.active_tasks.length}</span>
        </div>
        {stats.active_tasks.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-10">No active tasks. Time for a coffee ☕</p>
        ) : (
          <div className="divide-y divide-zinc-100">
            {stats.active_tasks.map((t, i) => (
              <Link
                key={t.id + i}
                to={`/projects/${t.project_id}`}
                data-testid={`dashboard-task-${t.id}`}
                className="flex items-center justify-between gap-3 py-3.5 group"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-zinc-950 truncate">{t.name}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {t.client_name} · <span className="text-zinc-700">{t.project_name}</span> · #{t.job_number}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={t.status} />
                  <ArrowRight className="w-4 h-4 text-zinc-300 group-hover:text-zinc-950 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </Shell>
  );
};

export default Dashboard;
